import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useUserContext } from '@/shared/contexts/userContext'
import { useWorkspace } from '@/shared/hooks/data-hooks/workspaces/useWorkspace'
import { useFeatureFlagsWithWorkspace } from '@/shared/hooks/useFeatureFlags'
import { useWorkspaceContext } from '@/shared/hooks/useWorkspaceContext'
import type { PlaybookUserAction } from '@/types/playbook'
import EditOutlined from '@mui/icons-material/EditOutlined'
import KeyboardArrowDownOutlined from '@mui/icons-material/KeyboardArrowDownOutlined'
import LanguageOutlined from '@mui/icons-material/LanguageOutlined'
import LockOutlined from '@mui/icons-material/LockOutlined'
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined'
import React, { ComponentProps, useState } from 'react'
import {
  PlaybookRenameModal,
  type RenameFormValues,
} from './PlaybookRenameModal'
import { PlaybookVisibilityModal } from './PlaybookVisibilityModal'

type VisibilityDropdownProps = {
  isLoading?: boolean
  isPublic?: boolean
  playbook?: { title: string; created_by?: string; playbook_id?: string }
  onPlaybookAction?: (action: PlaybookUserAction, payload?: unknown) => void
  onRename?: (data: RenameFormValues) => void
  triggerProps?: ComponentProps<typeof DropdownMenuTrigger>
  contentProps?: ComponentProps<typeof DropdownMenuContent>
}

export const VisibilityDropdown: React.FC<VisibilityDropdownProps> = ({
  isLoading,
  isPublic,
  playbook,
  onPlaybookAction,
  onRename,
  triggerProps,
  contentProps,
}) => {
  const { user } = useUserContext()
  const { workspaceId } = useWorkspaceContext()
  const { data: { data: workspace } = {}, isLoading: isLoadingWorkspace } =
    useWorkspace(workspaceId)
  const {
    flags: [isRenamingEnabled, isVisibilityManagementEnabled],
  } = useFeatureFlagsWithWorkspace([
    'playbookRenaming',
    'playbookVisibilityManagement',
  ])

  const isSomeOptionEnabled = isRenamingEnabled || isVisibilityManagementEnabled

  const [isVisibilityModalOpen, setIsVisibilityModalOpen] = useState(false)
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false)

  const isOwner = user?._id === playbook?.created_by

  return isLoadingWorkspace || isLoading ? (
    <Skeleton className="h-4 w-8 bg-grey-03 md:h-6" />
  ) : (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild {...triggerProps}>
          <Button
            variant="ghost"
            size="sm"
            className="flex cursor-pointer gap-1 px-2"
          >
            {isPublic ? (
              <LanguageOutlined sx={{ fontSize: 16 }} />
            ) : (
              <LockOutlined sx={{ fontSize: 16 }} />
            )}
            {isSomeOptionEnabled && (
              <KeyboardArrowDownOutlined sx={{ fontSize: 12 }} />
            )}
          </Button>
        </DropdownMenuTrigger>
        {isSomeOptionEnabled && (
          <DropdownMenuContent {...contentProps} align="start">
            {isRenamingEnabled && (
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => setIsRenameModalOpen(true)}
                disabled={!isOwner}
              >
                <EditOutlined sx={{ fontSize: 16 }} className="mr-2" />
                Rename
              </DropdownMenuItem>
            )}
            {isVisibilityManagementEnabled && (
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => {
                  setIsVisibilityModalOpen(true)
                }}
                disabled={!isOwner}
              >
                <VisibilityOutlined sx={{ fontSize: 16 }} className="mr-2" />
                Visibility settings
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        )}
      </DropdownMenu>
      <PlaybookVisibilityModal
        defaultValues={{
          visibility: isPublic ? 'public' : 'private',
        }}
        open={isVisibilityModalOpen}
        onOpenChange={setIsVisibilityModalOpen}
        plan={workspace?.subscription?.subscriptionPlan?.type}
        onSubmit={async (values) => {
          if (!values) return
          onPlaybookAction?.('visibility', {
            playbook,
            visibility: values.visibility,
          })
          setIsVisibilityModalOpen(false)
        }}
      />
      <PlaybookRenameModal
        playbook={playbook}
        isOpen={isRenameModalOpen}
        onOpenChange={setIsRenameModalOpen}
        onSubmit={async (values) => {
          if (values) {
            if (onRename) {
              onRename(values)
            } else {
              onPlaybookAction?.('rename', {
                playbook,
                newTitle: values.title,
              })
            }
          }
          setIsRenameModalOpen(false)
        }}
      />
    </>
  )
}
